import { parsePhoneNumberFromString } from 'libphonenumber-js';

import type { ICity } from '@/modules/common/model/city';
import type { User } from '@/modules/user/model';

import { IPartner, OnboardingStep } from './index';

/** The site's chauffeur landing passes the chosen city along the redirect. */
const LANDING_CITY_ID_KEY = 'city_id';

type PartnerCity = Pick<User, 'cityId' | 'cityName'>;

interface EmptyPartnerSeed {
  /** The city the partner starts with; the user's own unless the landing said otherwise. */
  city?: PartnerCity;
  /** The address the registration was given, for as long as Core does not know it. */
  email?: string;
}

const emptyAboutMe: User = {
  cityId: '',
  cityName: '',
  firstName: '',
  userId: '',
  lastName: '',
  middleName: '',
  phone: '',
  source: null,
  roles: [],
};

/**
 * The single onboarding city of the country the phone belongs to. Where a
 * country runs several, the choice stays the user's: naming the wrong city is
 * worse than leaving the field empty, which is what they came here to fill.
 */
const pickCityByPhoneCountry = (phone: string, cities: ICity[]): PartnerCity | undefined => {
  const country: string | undefined = phone
    ? parsePhoneNumberFromString(phone)?.country
    : undefined;

  if (!country) {
    return undefined;
  }

  const [only, ...rest] = cities.filter(city => city.countryCode === country);

  return only && rest.length === 0 ? { cityId: only.id, cityName: only.name } : undefined;
};

/**
 * The city an empty partner starts with. Core gives a brand-new user a city
 * guessed from the phone's country, so for them the city chosen on the landing
 * wins — as long as onboarding knows it. Anyone with a record of their own, a
 * chauffeur without a partner say, keeps the city that record carries.
 *
 * When Core guesses nothing, the phone is read here instead. An empty city is
 * not only an empty field: the region every step is branched on is looked up
 * from it, so a Dubai number with no city left the whole onboarding countryless.
 */
const pickEmptyPartnerCity = ({
  isNew,
  user,
  landingCityId,
  cities,
}: {
  isNew: boolean;
  user: User;
  landingCityId: string | undefined;
  cities: ICity[];
}): PartnerCity => {
  const landingCity =
    isNew && landingCityId ? cities.find(city => city.id === landingCityId) : undefined;

  if (landingCity) {
    return { cityId: landingCity.id, cityName: landingCity.name };
  }

  if (user.cityId) {
    return { cityId: user.cityId, cityName: user.cityName };
  }

  return (
    pickCityByPhoneCountry(user.phone, cities) ?? { cityId: user.cityId, cityName: user.cityName }
  );
};

const getEmptyPartner = (
  user = emptyAboutMe,
  { city = user, email }: EmptyPartnerSeed = {},
): IPartner => ({
  accessStatus: 'active',
  cityId: city.cityId,
  cityName: city.cityName,
  companyRegNumber: '',
  countryCode: undefined,
  docUploads: {},
  // Core's word first: the held address only fills a blank.
  email: user.email || email || '',
  firstName: user.firstName,
  hasIntegrationWithTaxService: false,
  id: '',
  inn: '',
  lastName: user.lastName,
  legalAddress1: '',
  legalKpp: '',
  legalName: '',
  legalNds: false,
  legalOgrn: '',
  legalRegisteredAddress: undefined,
  legalPostalAddress: undefined,
  middleName: user.middleName,
  onboarding: {
    isCompleted: false,
    lastStepName: OnboardingStep.welcome,
    isLastStepCompleted: false,
  },
  partnerType: undefined,
  paymentAccount: '',
  paymentBik: '',
  paymentIban: '',
  paymentRasAccount: '',
  paymentRoutingNumber: '',
  paymentSortCode: '',
  paymentSwift: '',
  ssn: '',
  ein: '',
  dbaName: '',
  licenseNumber: '',
  personalInn: '',
  paymentKorAccount: '',
  paymentBankName: '',
  phone: user.phone ?? emptyAboutMe.phone,
  referralCode: '',
  source: null,
  userId: user.userId,
  vatRegNo: '',
  isVehiclesHidden: false,
  taxRegNo: '',
  tradeLicenseNumber: '',
  registrationAuditStep: null,
  taxProfile: undefined,
  hasPhoLicense: null,
});

export { getEmptyPartner, emptyAboutMe, pickEmptyPartnerCity, LANDING_CITY_ID_KEY };
